import type { ButtonHTMLAttributes, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Tooltip } from '@elfsight/widget-modal';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';

type ToolbarButtonProps = PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    tooltip?: string;
  }
>;

export function ToolbarButton({
  children,
  tooltip,
  ...props
}: ToolbarButtonProps) {
  const { resultBackgroundColor } = useSettings();

  const iconColor = getInvertedColor(
    resultBackgroundColor,
    ['rgb(255, 255, 255)', 'rgb(17, 17, 17)'],
    180
  );
  const hoverBackgroundColor = getColorWithAlpha(iconColor, 0.08);

  return (
    <Tooltip title={tooltip}>
      <StyledButton
        $iconColor={iconColor}
        $hoverBackgroundColor={hoverBackgroundColor}
        {...props}
      >
        {children}
      </StyledButton>
    </Tooltip>
  );
}

const StyledButton = styled.button<{
  $iconColor: string;
  $hoverBackgroundColor: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: ${({ $iconColor }) => $iconColor};

  & svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background-color: ${({ $hoverBackgroundColor }) => $hoverBackgroundColor};

    svg {
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.95);
  }
`;
